import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../../../utils/config';

const REQUEST_TIMEOUT_MS = 20000;

const AttendanceModal = ({ isOpen, onClose, attendance, employees, onSave, mode = 'add' }) => {
  const [formData, setFormData] = useState({
    employee: '',
    date: '',
    attendance_data: {
      check_in_time: '',
      check_out_time: '',
      status: 'present',
      remarks: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (attendance && mode === 'edit') {
        setFormData({
          employee: attendance?.employee || '',
          date: attendance?.date || '',
          attendance_data: {
            check_in_time: attendance?.attendance_data?.check_in_time || '',
            check_out_time: attendance?.attendance_data?.check_out_time || '',
            status: attendance?.attendance_data?.status || 'present',
            remarks: attendance?.attendance_data?.remarks || ''
          }
        });
      } else {
        // Set today's date as default
        const today = new Date().toISOString().split('T')[0];
        setFormData({
          employee: '',
          date: today,
          attendance_data: {
            check_in_time: '',
            check_out_time: '',
            status: 'present',
            remarks: ''
          }
        });
      }
      setErrors({});
      setSuccessMessage('');
      setErrorMessage('');
    }
  }, [isOpen, attendance, mode]);

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
    
    // Clear error for this field
    if (errors[path]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[path];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.employee) {
      newErrors['employee'] = 'Employee is required';
    }
    if (!formData.date) {
      newErrors['date'] = 'Date is required';
    }
    if (!formData.attendance_data.status) {
      newErrors['attendance_data.status'] = 'Status is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setErrorMessage('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const payload = {
        employee: Number(formData.employee),
        date: formData.date,
        attendance_data: {
          check_in_time: formData.attendance_data.check_in_time || '',
          check_out_time: formData.attendance_data.check_out_time || '',
          status: formData.attendance_data.status,
          remarks: formData.attendance_data.remarks || ''
        }
      };

      let response;
      if (mode === 'edit' && attendance?.id) {
        response = await axios.put(
          API_ENDPOINTS.ATTENDANCE_UPDATE(attendance.id),
          payload,
          { headers, timeout: REQUEST_TIMEOUT_MS }
        );
      } else {
        response = await axios.post(
          API_ENDPOINTS.ATTENDANCE_CREATE,
          payload,
          { headers, timeout: REQUEST_TIMEOUT_MS }
        );
      }

      if (response.data) {
        setSuccessMessage(mode === 'edit' ? 'Attendance updated successfully' : 'Attendance created successfully');
        setTimeout(() => {
          onSave();
          onClose();
        }, 1000);
      }
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Failed to save attendance';
      setErrorMessage(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit Attendance' : 'Create Attendance'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <X size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-6">
            {successMessage && (
              <div className="p-3 rounded-md border border-green-300 bg-green-50 text-green-700">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <p className="text-sm font-medium">{successMessage}</p>
                </div>
              </div>
            )}
            {errorMessage && (
              <div className="p-3 rounded-md border border-red-300 bg-red-50 text-red-700">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <p className="text-sm">{errorMessage}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Employee <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.employee}
                  onChange={(e) => handleInputChange('employee', e.target.value)}
                  className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${errors.employee ? 'border-red-500' : ''}`}
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map((emp) => {
                    const empData = emp?.employee_data || {};
                    const empName = empData?.employee_name || empData?.basic_information?.full_name || empData?.basic_information?.employee_name || `Employee ${emp.id}`;
                    return (
                      <option key={emp.id} value={emp.id}>
                        {empName} ({empData?.employee_id || empData?.basic_information?.employee_id || emp.id})
                      </option>
                    );
                  })}
                </select>
                {errors.employee && (
                  <p className="mt-1 text-sm text-red-500">{errors.employee}</p>
                )}
              </div>
              
              <Input
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                error={errors.date}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Check In Time"
                type="time"
                value={formData.attendance_data.check_in_time}
                onChange={(e) => handleInputChange('attendance_data.check_in_time', e.target.value)}
              />
              
              <Input
                label="Check Out Time"
                type="time"
                value={formData.attendance_data.check_out_time}
                onChange={(e) => handleInputChange('attendance_data.check_out_time', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.attendance_data.status}
                onChange={(e) => handleInputChange('attendance_data.status', e.target.value)}
                className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${errors['attendance_data.status'] ? 'border-red-500' : ''}`}
                required
              >
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="late">Late</option>
                <option value="half_day">Half Day</option>
                <option value="leave">Leave</option>
              </select>
              {errors['attendance_data.status'] && (
                <p className="mt-1 text-sm text-red-500">{errors['attendance_data.status']}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Remarks
              </label>
              <textarea
                value={formData.attendance_data.remarks}
                onChange={(e) => handleInputChange('attendance_data.remarks', e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Enter remarks (optional)"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update Attendance' : 'Create Attendance'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttendanceModal;

